import { Component, inject, signal } from '@angular/core';
import { form, FormField, min, required } from '@angular/forms/signals';
import { PlatformBillingServiceDiscountObject } from '@libs/types/src/services/object-type/platform-billing/platform-billing-organization.object';
import { BaseDialogComponent } from '../../../../../../../components/kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../../../../../components/kit/input/date-input-v2/date-input.component';
import { SelectInputComponent } from '../../../../../../../components/kit/input/select-input-v2/select-input.component';
import { NumberInputComponent } from '../../../../../../../components/kit/input/text-input-v2/number-input.component';
import { successCreated, successUpdated } from '../../../../../content.error';
import { PlatformBillingDiscountFormModel, PlatformBillingSelectOption } from '../organization-platform-billing.types';
import { OrganizationPlatformBillingDiscountUpsertDialogStore } from './organization-platform-billing-discount-upsert-dialog.store';

@Component({
    selector: 'app-organization-platform-billing-discount-upsert-dialog',
    templateUrl: './organization-platform-billing-discount-upsert-dialog.component.html',
    providers: [OrganizationPlatformBillingDiscountUpsertDialogStore],
    imports: [FormField, SelectInputComponent, NumberInputComponent, DateInputComponent],
})
export class OrganizationPlatformBillingDiscountUpsertDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(OrganizationPlatformBillingDiscountUpsertDialogStore);
    protected readonly item = this.dialogConfig.data.item as PlatformBillingServiceDiscountObject | null;
    protected readonly contractOptions = this.dialogConfig.data.contractOptions as PlatformBillingSelectOption[];
    protected readonly contractId = (this.dialogConfig.data.contractId as number | null | undefined) ?? null;
    protected readonly prefix = 'organization.platformBilling.fields.';

    protected readonly formData = signal<PlatformBillingDiscountFormModel>({
        id: this.item?.id ?? null,
        contractId: this.item?.contractId ?? this.contractId,
        percentage: this.item?.percentage ?? null,
        validFrom: this.toDate(this.item?.validFrom ?? null),
        validTo: this.toDate(this.item?.validTo ?? null),
    });
    protected readonly formDef = form(this.formData, (schema) => {
        required(schema.contractId);
        required(schema.percentage);
        min(schema.percentage, 0.01);
        required(schema.validFrom);
    });

    public override async save(): Promise<void> {
        if (this.formDef().invalid()) {
            this.formDef().markAsTouched();
            return;
        }

        const value = this.formData();
        if (value.contractId === null || value.percentage === null || value.validFrom === null) return;
        const validFrom = this.toDateString(value.validFrom);
        if (!validFrom) return;
        const input = {
            contractId: value.contractId,
            percentage: value.percentage,
            validFrom,
            validTo: this.toDateString(value.validTo),
        };
        if (this.item) {
            await this.store.api.updatePlatformBillingDiscount({ id: this.item.id, ...input });
            successUpdated();
        } else {
            await this.store.api.createPlatformBillingDiscount(input);
            successCreated();
        }
        this.dialogRef.close(true);
    }

    private toDate(value: string | null): Date | null {
        return value ? new Date(`${value}T12:00:00`) : null;
    }

    private toDateString(value: Date | null): string | null {
        if (!value) return null;
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
